import React from "react";
import { motion } from "framer-motion";
import { Building2, TrendingUp, Globe, FileText, Calculator, Banknote, CreditCard } from "lucide-react";
import { Tooltip } from "react-tooltip";

import data from "../data/constructionData.json";

const KPISection = () => {
    const metrics = data.table_2_utah_economic_metrics;

    const stats = [
        {
            title: "Construction GDP (Utah)",
            value: metrics["Total Value of Construction Underway"],
            icon: Building2,
            color: "#4F46E5",
            description: "The total market value of all ongoing construction projects in Utah, representing the industry's contribution to the state's economy."
        },
        {
            title: "Industry Earnings",
            value: metrics["Construction Industry Earnings"],
            icon: Banknote,
            color: "#10B981",
            description: "Total wages, salaries, and benefits earned by workers and business owners within Utah's construction sector."
        },
        {
            title: "Annual Sector Spending",
            value: metrics["Construction Annual Spending ($)"],
            icon: CreditCard,
            color: "#EF4444",
            description: "Total amount spent on construction activities across Utah in a single year, including materials, labor, and overhead."
        },
        {
            title: "Construction Job Growth",
            value: metrics["Job Growth (Construction)"],
            icon: TrendingUp,
            color: "#F59E0B",
            description: "The percentage increase in construction-related jobs compared to the previous period, indicating sector expansion."
        },
        {
            title: "State Nominal GDP (All)",
            value: metrics["State Nominal GDP (All Sectors)"],
            icon: Globe,
            color: "#8b5cf6",
            description: "Utah's total economic output across all industries at current market prices, used to compare construction's impact to the whole economy."
        },
        {
            title: "New Building Permits",
            value: metrics["New Building Permits (units)"],
            icon: FileText,
            color: "#06b6d4",
            description: "Total number of authorized new housing and commercial units, serving as a leading indicator for future construction activity."
        },
        {
            title: "Permit Value (Est.)",
            value: metrics["New Building Permit Value $ (Est.)"],
            icon: Calculator,
            color: "#ec4899",
            description: "The estimated dollar value of all new building permits issued, representing future investment in Utah's built environment."
        },
    ];

    const renderValue = (value) => {
        if (typeof value !== 'string' || !value.includes('(')) {
            const isLongValue = String(value).length > 18;
            return (
                <span className={`${isLongValue ? 'text-lg sm:text-xl leading-snug break-words' : 'whitespace-nowrap'}`}>
                    {value}
                </span>
            );
        }
        const [main, extra] = value.split('(');
        return (
            <div className="flex flex-col">
                <span className="text-xl font-black">{main.trim()}</span>
                <span className="text-[10px] font-bold text-gray-400 -mt-1 uppercase tracking-tighter cursor-default">
                    ({extra.trim()}
                </span>
            </div>
        );
    };

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, idx) => (
                <React.Fragment key={idx}>
                    <motion.div
                        whileHover={{ y: -5, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                        className={`bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 transition-all cursor-help ${idx >= 4 ? 'lg:col-span-1' : ''}`}
                        data-tooltip-id={`kpi-tooltip-${idx}`}
                        data-tooltip-content={stat.description}
                    >
                        <div className="p-3 rounded-xl" style={{ backgroundColor: `${stat.color}15` }}>
                            <stat.icon size={22} color={stat.color} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h3 className="text-gray-400 text-[10px] font-bold uppercase tracking-widest">{stat.title}</h3>
                            <div className="mt-0.5 text-xl font-black text-gray-900 leading-tight min-w-0">
                                {renderValue(stat.value)}
                            </div>
                        </div>
                    </motion.div>
                    <Tooltip
                        id={`kpi-tooltip-${idx}`}
                        place="top"
                        style={{
                            backgroundColor: '#1e293b',
                            color: '#fff',
                            borderRadius: '8px',
                            fontSize: '12px',
                            padding: '8px 12px',
                            maxWidth: '240px',
                            zIndex: 1000,
                            fontWeight: 'normal',
                            lineHeight: '1.4'
                        }}
                    />
                </React.Fragment>
            ))}
        </div>
    );
};

export default KPISection;
